import type { AdminOrgDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { CompanyLogo } from './CompanyLogo';
import { CompanyStatusBadge } from './CompanyStatusBadge';
import { Routes } from '../../../routes/routes';
import { formatDate } from '../../../utils/format';
import { placeText } from '../../../utils/place';

/**
 * Company card header (AUT-403), after `companies-detail.html`: breadcrumb,
 * logo, name + status (+ «демо»), and «юр. имя · УНП · город · создана».
 * The creating admin's name is not on the wire, so it is not shown.
 */
export const CompanyHeader = ({ org }: { org: AdminOrgDto }): ReactElement => {
  const { t } = useTranslation();
  const meta = [
    org.legalName,
    org.unp ? t('companies.detail.unp', { unp: org.unp }) : null,
    placeText(org),
    t('companies.detail.createdAt', { date: formatDate(org.createdAt) }),
  ].filter((part): part is string => Boolean(part));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        <RouterLink to={Routes.App.Companies}>{t('companies.title')}</RouterLink> / {org.name}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
        <CompanyLogo name={org.name} logoUrl={org.logoUrl} size={52} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
              {org.name}
            </Typography>
            <CompanyStatusBadge status={org.status} />
            {org.isDemo ? <Chip size="small" color="info" label={t('companies.demo')} /> : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {meta.join(' · ')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
